import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import { TextArea, radius } from "@libertify/ui";
import styled from "styled-components";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import StepHeader from "./StepHeader";
import { GUARDRAIL_EXAMPLE_KEYS, I18N_PREFIX } from "./constants";

// backend free-text fields cap at 4096 chars.
const MAX_FREE_TEXT_LENGTH = 4096;

const GuardrailStep = () => {
  const t = useTranslate();
  const { answers, stageAnswer } = useTeacherOnboarding();
  const [text, setText] = useState(answers.guardrail);

  const update = (next: string) => {
    setText(next);
    stageAnswer({ guardrail: next });
  };

  const addExample = (example: string) => {
    const cur = text.trim();
    const sep = cur && !/[.!?»]$/.test(cur) ? "." : "";
    update(cur ? `${cur}${sep} ${example}` : example);
  };

  return (
    <Flex vertical align="center" gap={14} style={{ width: "100%" }}>
      <StepHeader i18nBase="guardrail" />

      <Trust>
        <TrustTitle>{t(`${I18N_PREFIX}.guardrail.protectTitle`)}</TrustTitle>
        <Typography.Paragraph type="secondary" style={{ margin: "0 0 4px" }}>
          {t(`${I18N_PREFIX}.guardrail.description`)}
        </Typography.Paragraph>

        <EgLabel>{t(`${I18N_PREFIX}.guardrail.examplesLabel`)}</EgLabel>
        <Flex wrap="wrap" gap={8} style={{ marginBottom: 14 }}>
          {GUARDRAIL_EXAMPLE_KEYS.map((key) => {
            const example = t(`${I18N_PREFIX}.guardrail.examples.${key}`);
            return (
              <EgChip
                key={key}
                type="button"
                onClick={() => addExample(example)}
              >
                {example}
              </EgChip>
            );
          })}
        </Flex>

        <GuardTextArea
          value={text}
          onChange={(e) => update(e.target.value)}
          placeholder={t(`${I18N_PREFIX}.guardrail.placeholder`)}
          maxLength={MAX_FREE_TEXT_LENGTH}
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </Trust>

      <Typography.Paragraph
        type="secondary"
        style={{
          margin: 0,
          maxWidth: 560,
          textAlign: "center",
          fontSize: 12.5,
        }}
      >
        {t(`${I18N_PREFIX}.guardrail.hint`)}
      </Typography.Paragraph>
    </Flex>
  );
};

export default GuardrailStep;

// ─── Styled components ───

const Trust = styled.div`
  width: 100%;
  max-width: 560px;
  border: 1px solid ${({ theme }) => theme.colorPrimaryBorder};
  background: ${({ theme }) => theme.colorPrimaryBg};
  border-radius: ${radius.box}px;
  padding: 19px 21px;
`;

const TrustTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 16px;
  color: ${({ theme }) => theme.colorPrimary};
`;

const EgLabel = styled.div`
  margin: 12px 0 9px;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const EgChip = styled.button`
  max-width: 100%;
  text-align: left;
  font-size: 12.5px;
  line-height: 1.35;
  padding: 8px 11px;
  border-radius: 11px;
  cursor: pointer;
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorder};
  color: ${({ theme }) => theme.colorText};
  transition: 0.15s;

  &::before {
    content: "+ ";
    font-weight: 700;
    color: ${({ theme }) => theme.colorPrimary};
  }
  &:hover {
    border-color: ${({ theme }) => theme.colorPrimary};
    box-shadow: 0 2px 10px rgba(31, 181, 172, 0.14);
  }
`;

const GuardTextArea = styled(TextArea)`
  && {
    border-style: dashed;
  }
  &&:focus,
  &&:focus-within {
    border-style: solid;
  }
`;
